import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  LogOut,
  Download,
  Search,
  RefreshCw,
  Inbox,
  Flame,
  Users,
  Mail,
  Phone,
  Clock,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { API, authHeaders, clearToken, LEAD_STATUSES, formatApiError } from "../../lib/admin";

const priorityStyle = {
  High: "bg-red-50 text-red-700 border-red-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Low: "bg-slate-100 text-slate-600 border-slate-200",
};

const statusStyle = {
  New: "bg-water-pale text-water border-water/20",
  Contacted: "bg-blue-50 text-blue-700 border-blue-200",
  "Due Diligence Sent": "bg-indigo-50 text-indigo-700 border-indigo-200",
  Negotiating: "bg-purple-50 text-purple-700 border-purple-200",
  "Offer Received": "bg-teal-50 text-teal-700 border-teal-200",
  "Under Contract": "bg-amber-50 text-amber-700 border-amber-200",
  Closed: "bg-forest/10 text-forest border-forest/20",
  Archived: "bg-slate-100 text-slate-500 border-slate-200",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value, tone, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-warm-border p-5 flex items-center gap-4">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tone}`}>
      <Icon size={20} />
    </div>
    <div>
      <div className="text-3xl font-sans font-semibold text-navy leading-none tabular-nums">{value}</div>
      <div className="text-xs text-warm mt-1.5">{label}</div>
    </div>
  </div>
);

export const AdminDashboard = ({ onLogout }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (search) params.search = search;
      if (statusFilter !== "all") params.lead_status = statusFilter;
      if (priorityFilter !== "all") params.priority = priorityFilter;
      const { data } = await axios.get(`${API}/admin/inquiries`, {
        headers: authHeaders(),
        params,
      });
      setInquiries(data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, priorityFilter, sort, onLogout]);

  useEffect(() => {
    const t = setTimeout(fetchInquiries, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchInquiries, search]);

  const updateStatus = async (id, lead_status) => {
    try {
      await axios.patch(
        `${API}/admin/inquiries/${id}`,
        { lead_status },
        { headers: authHeaders() }
      );
      setInquiries((prev) => prev.map((q) => (q.id === id ? { ...q, lead_status } : q)));
      if (selected?.id === id) setSelected((s) => ({ ...s, lead_status }));
      toast.success(`Status updated to “${lead_status}”.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed.");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/inquiries/export`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bqs-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Leads exported to CSV.");
    } catch {
      toast.error("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    onLogout();
  };

  const total = inquiries.length;
  const newCount = inquiries.filter((q) => q.lead_status === "New").length;
  const highCount = inquiries.filter((q) => q.priority === "High").length;

  return (
    <div className="min-h-screen bg-warm-light" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div>
            <div className="label-caps text-[10px] text-water/70 tracking-[0.2em]">Bay Quarter Shores</div>
            <h1 className="font-serif text-2xl font-light mt-1">Lead Management</h1>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-white/80 hover:text-white border border-white/20 rounded-full px-5 py-2.5 transition-colors hover:bg-white/10"
          >
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Users} label="Total Leads" value={total} tone="bg-water-pale text-water" testid="stat-total" />
          <StatCard icon={Inbox} label="New (Unactioned)" value={newCount} tone="bg-blue-50 text-blue-600" testid="stat-new" />
          <StatCard icon={Flame} label="High Priority" value={highCount} tone="bg-red-50 text-red-600" testid="stat-high" />
        </div>

        {/* Controls */}
        <div className="bg-white rounded-2xl border border-warm-border p-4 mb-6 flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-warm" />
            <Input
              data-testid="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone, reference..."
              className="pl-10 h-11 rounded-xl"
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="admin-filter-status" className="w-[170px] h-11 rounded-xl">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {LEAD_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger data-testid="admin-filter-priority" className="w-[150px] h-11 rounded-xl">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="admin-sort" className="w-[150px] h-11 rounded-xl">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
              </SelectContent>
            </Select>
            <button
              data-testid="admin-refresh-btn"
              onClick={fetchInquiries}
              className="h-11 w-11 flex items-center justify-center rounded-xl border border-warm-border text-warm hover:text-navy hover:border-navy/30 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="admin-export-btn"
              onClick={exportCsv}
              disabled={exporting || total === 0}
              className="h-11 flex items-center gap-2 rounded-xl bg-navy text-white px-5 text-sm font-medium hover:bg-navy-light transition-colors disabled:opacity-50"
            >
              {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              Export CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-warm-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-inquiries-table">
              <thead>
                <tr className="bg-warm-light/70 text-left text-warm text-xs uppercase tracking-wide">
                  <th className="px-5 py-4 font-medium">Reference</th>
                  <th className="px-5 py-4 font-medium">Contact</th>
                  <th className="px-5 py-4 font-medium">Priority</th>
                  <th className="px-5 py-4 font-medium">Submitted</th>
                  <th className="px-5 py-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center text-warm">
                      <Loader2 size={22} className="animate-spin inline" />
                    </td>
                  </tr>
                ) : inquiries.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center text-warm" data-testid="admin-empty-state">
                      No leads found.
                    </td>
                  </tr>
                ) : (
                  inquiries.map((q) => (
                    <tr
                      key={q.id}
                      data-testid={`inquiry-row-${q.reference_number}`}
                      className="border-t border-warm-border/70 hover:bg-warm-light/40 transition-colors cursor-pointer"
                      onClick={() => setSelected(q)}
                    >
                      <td className="px-5 py-4 font-mono text-xs text-navy whitespace-nowrap">{q.reference_number || "—"}</td>
                      <td className="px-5 py-4">
                        <div className="font-medium text-navy">{q.name || "—"}</div>
                        <div className="text-xs text-warm flex items-center gap-1 mt-0.5">
                          <Mail size={11} /> {q.email}
                        </div>
                        {q.phone && (
                          <div className="text-xs text-warm flex items-center gap-1 mt-0.5">
                            <Phone size={11} /> {q.phone}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-block text-xs px-2.5 py-1 rounded-full border ${priorityStyle[q.priority] || priorityStyle.Medium}`}>
                          {q.priority}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs text-warm whitespace-nowrap">{fmtDate(q.created_at)}</td>
                      <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                        <Select value={q.lead_status} onValueChange={(v) => updateStatus(q.id, v)}>
                          <SelectTrigger
                            data-testid={`status-select-${q.reference_number}`}
                            className={`h-9 w-[168px] rounded-lg text-xs border ${statusStyle[q.lead_status] || ""}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LEAD_STATUSES.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif font-light text-2xl text-navy flex items-center gap-3">
                  {selected.name}
                  <span className={`text-xs px-2.5 py-1 rounded-full border font-sans ${priorityStyle[selected.priority]}`}>
                    {selected.priority}
                  </span>
                </DialogTitle>
                <DialogDescription className="text-warm">
                  Lead details for reference {selected.reference_number}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="font-mono text-xs text-water">{selected.reference_number}</div>
                <div className="grid grid-cols-2 gap-3">
                  <Detail label="Email" value={selected.email} />
                  <Detail label="Phone" value={selected.phone || "—"} />
                  <Detail label="Inquiry Type" value={selected.inquiry_type} />
                  <Detail label="Consent" value={selected.consent ? "Yes" : "No"} />
                </div>
                <div>
                  <div className="text-xs text-warm mb-1">Message</div>
                  <div className="bg-warm-light rounded-xl p-4 text-navy leading-relaxed whitespace-pre-wrap">
                    {selected.message || "—"}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-warm-border">
                  <Detail label="Submitted" value={fmtDate(selected.created_at)} />
                  <Detail label="Email Delivered" value={selected.email_sent ? "Yes" : "No"} />
                  <Detail label="Browser" value={selected.browser || "—"} />
                  <Detail label="OS" value={selected.operating_system || "—"} />
                  <Detail label="IP Address" value={selected.ip_address || "—"} />
                </div>
                <div className="flex items-center gap-1 text-xs text-warm pt-1">
                  <Clock size={12} /> Ref {selected.reference_number}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <div className="text-xs text-warm">{label}</div>
    <div className="text-navy break-words">{value}</div>
  </div>
);
